import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator as CalcIcon, DollarSign, TrendingUp, PieChart, BarChart3, FileSpreadsheet } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';


export default function FeeTableCalculator() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [inputs, setInputs] = useState({
    annual_transactions: 121046,
    service_rate: 2.2,
    service_id: '',
    proposed_fee_rate: ''
  });
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    }
  };

  const handleServiceChange = async (serviceName) => {
    try {
      const response = await api.get('/admin/services');
      
      const service = response.data.services.find(s => s.name === serviceName);
      if (service) {
        setSelectedService(service);
        setInputs(prev => ({ ...prev, service_id: service.id }));
      }
    } catch (error) {
      console.error('Error fetching service details:', error);
    }
  };

  const handleCalculate = async () => {
    if (!inputs.service_id) {
      toast.error('Please select a service');
      return;
    }

    try {
      setCalculating(true);
      
      const requestData = {
        annual_transactions: parseFloat(inputs.annual_transactions),
        service_rate: parseFloat(inputs.service_rate) / 100,
        service_id: inputs.service_id
      };
      
      // Add proposed fee rate if provided
      if (inputs.proposed_fee_rate && inputs.proposed_fee_rate !== '') {
        requestData.proposed_fee_rate = parseFloat(inputs.proposed_fee_rate) / 100;
      }
      
      const response = await api.post(
        '/fee-table/calculate',
        requestData
      );
      
      setResults(response.data);
      toast.success('Calculation complete!');
    } catch (error) {
      console.error('Error calculating:', error);
      toast.error('Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/calculator')}
              className="text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Fee Table Calculator</h1>
              <p className="text-sm text-slate-600">Revenue Projection & Analytics</p>
            </div>
            <button
              onClick={() => navigate('/fee-analytics-static')}
              className="btn-secondary text-sm ml-auto"
              title="View Full Static Analytics"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Full Excel View
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Panel */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-24">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Input Parameters</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Service Type
                  </label>
                  <SearchableSelect
                    options={services}
                    value={selectedService?.name || ''}
                    onChange={handleServiceChange}
                    placeholder="Select service..."
                    searchPlaceholder="Search services..."
                  />
                  {selectedService && (
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedService.collection_amounts?.length || 0} collection ranges
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Annual Transactions
                  </label>
                  <input
                    type="number"
                    value={inputs.annual_transactions}
                    onChange={(e) => setInputs({ ...inputs, annual_transactions: e.target.value })}
                    className="input-field w-full"
                    placeholder="121046"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Service Rate (%)
                  </label>
                  <input
                    type="number"
                    value={inputs.service_rate}
                    onChange={(e) => setInputs({ ...inputs, service_rate: e.target.value })}
                    className="input-field w-full"
                    step="0.1"
                    placeholder="2.2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Current: {inputs.service_rate}% ({(parseFloat(inputs.service_rate) / 100).toFixed(4)})
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Proposed Fee Rate (%) <span className="text-slate-400 font-normal">- Optional</span>
                  </label>
                  <input
                    type="number"
                    value={inputs.proposed_fee_rate}
                    onChange={(e) => setInputs({ ...inputs, proposed_fee_rate: e.target.value })}
                    className="input-field w-full"
                    step="0.1"
                    placeholder="e.g., 2.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Leave empty to skip validation
                  </p>
                </div>

                <button
                  onClick={handleCalculate}
                  disabled={calculating || !inputs.service_id}
                  className="btn-primary w-full"
                >
                  {calculating ? (
                    <span className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Calculating...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <CalcIcon className="w-4 h-4 mr-2" />
                      Calculate
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {!results ? (
              <div className="card p-12 text-center">
                <CalcIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-600 mb-2">Ready to Calculate</h3>
                <p className="text-slate-500">
                  Select a service and enter parameters to see revenue projections
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Validation Status - Show if proposed fee was provided */}
                {results.validation && (
                  <div className={`card p-6 border-2 ${results.validation.is_acceptable ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 mb-1">Fee Acceptance Validation</h3>
                        <p className="text-sm text-slate-600">
                          {results.validation.is_acceptable 
                            ? '✅ Proposed fee is acceptable based on routing and minimums'
                            : '❌ Proposed fee does not meet requirements'}
                        </p>
                      </div>
                      <div className={`px-4 py-2 rounded-lg font-bold ${results.validation.is_acceptable ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                        {results.validation.is_acceptable ? 'ACCEPTED' : 'REJECTED'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-slate-600 mb-1">Proposed Rate</div>
                        <div className="text-lg font-semibold text-slate-900">
                          {results.validation.proposed_rate.toFixed(2)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-600 mb-1">Current Effective Rate</div>
                        <div className="text-lg font-semibold text-slate-900">
                          {results.validation.current_effective_rate.toFixed(2)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-600 mb-1">Revenue Impact</div>
                        <div className={`text-lg font-semibold ${results.validation.revenue_difference >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {results.validation.revenue_difference >= 0 ? '+' : ''}{formatCurrency(results.validation.revenue_difference)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-600 mb-1">Difference %</div>
                        <div className={`text-lg font-semibold ${results.validation.revenue_difference_percent >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {results.validation.revenue_difference_percent >= 0 ? '+' : ''}{results.validation.revenue_difference_percent.toFixed(1)}%
                        </div>
                      </div>
                    </div>

                    {!results.validation.minimums_acceptable && results.validation.minimum_violations.length > 0 && (
                      <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 mb-2">Minimum Fee Violations:</h4>
                        <div className="space-y-2">
                          {results.validation.minimum_violations.map((violation, idx) => (
                            <div key={idx} className="text-sm text-red-800">
                              <span className="font-medium">{violation.payment_method}:</span> Proposed ${violation.proposed_minimum.toFixed(2)} below required ${violation.current_minimum.toFixed(2)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="card p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-700">Total Revenue</span>
                      <DollarSign className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-2xl font-bold text-blue-900">
                      {formatCurrency(results.summary.total_revenue)}
                    </div>
                  </div>

                  <div className="card p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-700">Avg Payment Fee</span>
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-green-900">
                      {formatCurrency(results.summary.avg_payment_fee)}
                    </div>
                  </div>

                  <div className="card p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-700">Gross Take Rate</span>
                      <PieChart className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-2xl font-bold text-purple-900">
                      {results.summary.gross_take_rate.toFixed(2)}%
                    </div>
                  </div>
                </div>

                {/* Payment Method Breakdown */}
                {results.payment_method_totals && (
                  <div className="card p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Revenue by Payment Method</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(results.payment_method_totals).map(([method, data]) => (
                        <div key={method} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <div className="text-sm font-medium text-slate-600 mb-2">{method}</div>
                          <div className="text-xl font-bold text-slate-900 mb-1">
                            {formatCurrency(data.revenue)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatNumber(data.transactions)} txns
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Metrics */}
                <div className="card p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Summary Metrics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-slate-600">Total Collections</div>
                      <div className="text-lg font-semibold text-slate-900">
                        {formatCurrency(results.summary.total_collections)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-600">Avg Collection/Transaction</div>
                      <div className="text-lg font-semibold text-slate-900">
                        {formatCurrency(results.summary.avg_collection_per_transaction)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-600">Annual Transactions</div>
                      <div className="text-lg font-semibold text-slate-900">
                        {formatNumber(results.inputs.annual_transactions)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-600">Service Rate</div>
                      <div className="text-lg font-semibold text-slate-900">
                        {results.summary.service_rate_percent.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tier Breakdown */}
                <div className="card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-900">Revenue by Collection Tier</h3>
                    <BarChart3 className="w-5 h-5 text-slate-500" />
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 font-semibold text-slate-700">Range</th>
                          <th className="text-right py-2 font-semibold text-slate-700">% Mix</th>
                          <th className="text-right py-2 font-semibold text-slate-700">Transactions</th>
                          <th className="text-right py-2 font-semibold text-slate-700">Collections</th>
                          <th className="text-right py-2 font-semibold text-slate-700">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.tier_breakdown.map((tier, idx) => (
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 font-medium text-slate-900">{tier.range}</td>
                            <td className="text-right py-2 text-slate-600">
                              {tier.percent_of_mix.toFixed(2)}%
                            </td>
                            <td className="text-right py-2 text-slate-600">
                              {formatNumber(tier.transactions)}
                            </td>
                            <td className="text-right py-2 text-slate-600">
                              {formatCurrency(tier.collections)}
                            </td>
                            <td className="text-right py-2 font-semibold text-slate-900">
                              {formatCurrency(tier.revenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-slate-300">
                          <td className="py-2 font-bold text-slate-900">Total</td>
                          <td className="text-right py-2 font-bold text-slate-900">100%</td>
                          <td className="text-right py-2 font-bold text-slate-900">
                            {formatNumber(results.summary.total_transactions)}
                          </td>
                          <td className="text-right py-2 font-bold text-slate-900">
                            {formatCurrency(results.summary.total_collections)}
                          </td>
                          <td className="text-right py-2 font-bold text-slate-900">
                            {formatCurrency(results.summary.total_revenue)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
